export const defaultCaseBrief = `
## 1. Decision
**Decision Question:** What decision are you trying to make?

**Decision Format:** Whether to proceed | Choose between options | Resource allocation | Other

**Decision Scope:** What is in scope for this analysis? What is explicitly out of scope?

**Strategic Intent:** What business outcome or pressure is driving this? If the question evolved from a broader starting point, note the shift.

## 2. Context
### Current State & Background
What's the situation? What exists today?

### Problem Statement
What specific problem are you trying to solve?

### Evidence Base
What data, research, or evidence do you already have?

### Market Context & Timing
Why is this decision important now? What external factors are relevant?

### Strategic Intent
How does this decision fit into the broader strategy?

## 3. Key Questions
Each question has a priority (BLOCKING / HIGH / MEDIUM) and source (RESEARCH = we analyze / CLIENT = stakeholder answers).

### Category 1: [e.g., Market Viability]
- [BLOCKING/RESEARCH] Question 1
- [HIGH/RESEARCH] Question 2

### Category 2: [e.g., Technical Feasibility]
- [HIGH/RESEARCH] Question 3

## 4. Success Criteria
### Primary Success Criteria
- What quantitative thresholds must be met? (e.g., revenue targets, margins, market share)
- What market validation signals are required?

### Secondary Success Criteria
- What qualitative outcomes are desired?

### Evaluation Guidance
How should trade-offs be assessed? What is the critical threshold at which you should not proceed?

## 5. Assumptions
Uncertain beliefs that must hold true for success. Each should be stress-testable and could plausibly be wrong. Not constraints, not evaluation criteria, not process steps.
- Assumption 1: [description]
- Assumption 2: [description]

## 6. Constraints
Fixed, immutable boundary conditions that cannot be changed within this decision.
- **[Dimension]**: [description and implication]

## 7. Stakeholders & Background
### Key Stakeholders
- Who are the decision makers?
- Who is affected?

### Project Stage & Prior Work
- What stage is this initiative at?
- What has been done before?
`;

export const defaultCaseBriefJapanese = `
## 1. 意思決定
**意思決定の問い:** どのような意思決定を行おうとしていますか？

**意思決定の形式:** 進めるか否か | 選択肢から選ぶ | リソース配分 | その他

**意思決定の範囲:** この分析の対象範囲は何ですか？明確に対象外とするものは何ですか？

**戦略的意図:** どのようなビジネス上の成果やプレッシャーがこれを推進していますか？問いがより広い出発点から発展した場合は、その変化を記載してください。

## 2. コンテキスト
### 現状と背景
現在の状況はどうなっていますか？今日、何が存在していますか？

### 課題定義
解決しようとしている具体的な問題は何ですか？

### エビデンス基盤
すでに持っているデータ、調査、エビデンスは何ですか？

### 市場コンテキストとタイミング
なぜ今この意思決定が重要なのですか？関連する外部要因は何ですか？

### 戦略的意図
この意思決定は、より広い戦略にどのように適合しますか？

## 3. 重要な問い
各問いには優先度（BLOCKING / HIGH / MEDIUM）とソース（RESEARCH = 分析対象 / CLIENT = ステークホルダーが回答）があります。

### カテゴリー 1: [例：市場の実現可能性]
- [BLOCKING/RESEARCH] 問い 1
- [HIGH/RESEARCH] 問い 2

### カテゴリー 2: [例：技術的実現可能性]
- [HIGH/RESEARCH] 問い 3

## 4. 成功基準
### 主要成功基準
- 達成すべき定量的閾値は何ですか？（例：収益目標、利益率、市場シェア）
- 必要な市場検証シグナルは何ですか？

### 副次的成功基準
- 望ましい定性的な成果は何ですか？

### 評価ガイダンス
トレードオフはどのように評価すべきですか？進めないと判断すべき重要な閾値は何ですか？

## 5. 前提条件
成功のために成立しなければならない不確実な信念。ストレステスト可能で、誤りである可能性がもっともらしいもの。制約条件、評価基準、プロセスステップではありません。
- 前提条件 1: [説明]
- 前提条件 2: [説明]

## 6. 制約条件
この意思決定の中で変更できない固定的で不変の境界条件。
- **[次元]**: [説明と影響]

## 7. ステークホルダーと背景
### 主要ステークホルダー
- 意思決定者は誰ですか？
- 影響を受けるのは誰ですか？

### プロジェクトの段階と過去の取り組み
- このイニシアチブはどの段階にありますか？
- これまでに何が行われましたか？
`;

// Lines of an earlier skeleton that saved briefs still carry; they count as template text.
export const retiredTemplateLines = [
  "**Decision Format:** Go / No-Go | Choose between options | Resource allocation | Other",
  "How should trade-offs be assessed? What is the critical threshold for a No-Go?",
  "**意思決定の形式:** Go / No-Go | 選択肢から選ぶ | リソース配分 | その他",
  "トレードオフはどのように評価すべきですか？No-Goの重要な閾値は何ですか？"
];

export function getDefaultCaseBrief(language: string): string {
  switch (language.toLowerCase()) {
    case "japanese":
      return defaultCaseBriefJapanese;
    default:
      return defaultCaseBrief;
  }
}
